/**
 * Customer Health panel (Super Admin) — what the engagement overview says, in plain words.
 *
 * The numbers and the verdicts come from the server (teamio-serverless-api/lifecycle-service,
 * POST /get-engagement-overview) and are NEVER recomputed here: every threshold quoted in a sentence is
 * read from the `rules` object the response echoes (plan decision 2.2 — the UI hardcodes nothing).
 * This file only chooses labels, colours and wording.
 *
 * COPY RULE (plan §10): a site that has never created a follow-up and is past the "stalled" mark is shown
 * as "No follow-ups yet", not "Onboarding" — a practice that signed up a year ago is not onboarding. The
 * server still says status "onboarding"; the summary counts here are therefore taken from the rows, using
 * the same display keys, so a chip never disagrees with the rows under it.
 *
 * Import-free: cypress/node-tests/customer-health-panel.test.js executes this file verbatim.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Display keys, in the order the summary chips are shown. `variant` is a Bootstrap colour. */
export const DISPLAY_STATUSES = [
  { key: 'at_risk', label: 'At risk', variant: 'danger' },
  { key: 'watch', label: 'Watch', variant: 'warning' },
  { key: 'no_followups', label: 'No follow-ups yet', variant: 'warning' },
  { key: 'onboarding', label: 'Onboarding', variant: 'info' },
  { key: 'low_activity', label: 'Low activity', variant: 'secondary' },
  { key: 'healthy', label: 'Healthy', variant: 'success' },
  { key: 'error', label: "Couldn't check", variant: 'dark' },
];

const BY_KEY = DISPLAY_STATUSES.reduce((acc, s) => { acc[s.key] = s; return acc; }, {});

/** "2026-10-02" -> "Oct 2, 2026". Pure string work: no Date object, so no time-zone shift. */
export function formatDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof day === 'string' ? day : '');
  if (!m) return '—';
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return '—';
  return `${month} ${Number(m[3])}, ${m[1]}`;
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** "today" / "1 business day ago" / "12 business days ago". null when the count is not usable. */
export function businessDaysAgo(n) {
  if (typeof n !== 'number' || !isFinite(n) || n < 0) return null;
  return n === 0 ? 'today' : `${plural(n, 'business day')} ago`;
}

/** Which display key a row falls under. The server's `status` plus the stalled-onboarding copy rule. */
export function displayKey(site) {
  if (!site || !site.status) return 'error';
  if (site.status === 'onboarding' && site.onboardingStage === 'stalled') return 'no_followups';
  return BY_KEY[site.status] ? site.status : 'error';
}

/** Plain-language form of the failure codes the server can return for a site or a feature. */
export function errorText(code) {
  switch (code) {
    case 'timeout': return 'a query took too long';
    case 'deadline': return 'the check ran out of time before reaching it';
    case 'query_failed': return 'a query failed';
    case 'bad_data': return 'the dates on record were unusable';
    case 'no_data': return 'no data came back';
    default: return code ? String(code) : 'unknown problem';
  }
}

/**
 * One row's status as shown: `{ key, label, variant, reason }`.
 * @param {Object} site   a row of response.sites
 * @param {Object} rules  response.rules (thresholds actually applied today, onboarding marks, lookback)
 */
export function describeStatus(site, rules) {
  const key = displayKey(site);
  const meta = BY_KEY[key];
  const r = rules || {};
  const t = r.thresholds || {};
  const o = r.onboarding || {};
  const last = formatDay(site && site.lastFollowupDay);
  const ago = businessDaysAgo(site && site.quietBusinessDays);
  const lastLine = ago ? `Last follow-up ${last} (${ago}).` : `Last follow-up ${last}.`;
  const relaxed = t.holidayRelaxed ? ' Holiday relaxation is applied to the limits right now.' : '';
  const age = site && site.siteAgeDays;
  let reason;

  switch (key) {
    case 'error':
      reason = `Could not be checked: ${errorText(site && site.error)}. Left out of the totals.`;
      break;
    case 'no_followups':
      reason = typeof age === 'number'
        ? `Signed up ${plural(age, 'day')} ago and has never created a follow-up.`
        : 'Has never created a follow-up.';
      break;
    case 'onboarding':
      if (site.onboardingStage === 'unknown' || typeof age !== 'number') {
        reason = "No follow-up yet, and the sign-up date isn't on record.";
      } else if (site.onboardingStage === 'slow') {
        reason = `${plural(age, 'day')} since sign-up with no follow-up yet (a first one is expected within ${plural(o.slowDay, 'day')}).`;
      } else {
        reason = `Day ${age} since sign-up, no follow-up yet (a first one is expected within ${plural(o.slowDay, 'day')}).`;
      }
      break;
    case 'at_risk':
      reason = `${lastLine} At risk past ${plural(t.atRiskQuietBusinessDays, 'business day')}.${relaxed}`;
      if (site.established === false) {
        reason += site.activeDays180 === 0
          ? ` No active days with follow-ups in the last ${r.establishedLookbackDays}.`
          : ` Only ${plural(site.activeDays180, 'active day')} with follow-ups in the last ${r.establishedLookbackDays}.`;
      }
      break;
    case 'watch':
      if (site.reason === 'decline_with_quiet_stretch') {
        const pct = typeof site.declineRatio === 'number' ? Math.round(site.declineRatio * 100) : null;
        reason = pct == null
          ? `Follow-ups have dropped well below their earlier pace. ${lastLine}`
          : `Follow-ups are down to ${pct}% of their earlier pace. ${lastLine}`;
      } else {
        reason = `${lastLine} Watch past ${plural(t.watchQuietBusinessDays, 'business day')}.${relaxed}`;
      }
      break;
    case 'low_activity':
      reason = `Only ${plural(site.activeDays180, 'active day')} with follow-ups in the last ${r.establishedLookbackDays} (judged on the Watch rule from ${r.establishedMinActiveDays}). ${lastLine}`;
      break;
    default: // healthy
      reason = `${lastLine} Within its normal pattern.`;
  }

  return { key, label: meta.label, variant: meta.variant, reason };
}

/**
 * THE CHECKS, in the order the server asks them (lifecycle-service/lib/evaluate.js, "first match wins").
 * ONE list feeds both the per-row "What we checked" and the legend under the table, so the two can never
 * describe different rules. Every number is read from the echoed `rules`; only the wording lives here.
 *
 * `decides(site)` says whether this check is the one that set the row's status. `hitStatus` is the display
 * key a row gets when it is. The server's verdict is never recomputed: `decides` only READS `site.status` /
 * `site.reason`, so an explanation can't contradict the badge (the tests pin that for every status).
 */
const limits = (rules) => {
  const r = rules || {};
  const t = r.thresholds || {};
  const o = r.onboarding || {};
  return { r, t, o };
};
/**
 * How the decline check names its two windows. The server echoes `declineRecentDays` / `declinePriorDays` (the
 * windows behind the ratio); a server that does not send them yet (older deploy) gets the generic wording, so a
 * frontend deployed first still reads correctly.
 */
function paceTerms(rules) {
  const r = rules || {};
  const ok = (n) => typeof n === 'number' && isFinite(n) && n > 0;
  if (ok(r.declineRecentDays) && ok(r.declinePriorDays)) {
    return {
      recent: `its follow-up pace over the last ${plural(r.declineRecentDays, 'day')}`,
      earlier: `its pace over the ${plural(r.declinePriorDays, 'day')} before that`,
      priorPeriod: `in the ${plural(r.declinePriorDays, 'day')} before the last ${plural(r.declineRecentDays, 'day')}`,
    };
  }
  return { recent: 'its recent pace', earlier: 'its earlier pace', priorPeriod: 'in the earlier period' };
}
const upFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const relaxedNote =(t) => (t.holidayRelaxed ? ` (holiday relaxation applied: the limit is ${t.multiplier} times its usual size right now)` : '');

const CHECK_STEPS = [
  {
    id: 'readable',
    question: 'Could we read its follow-up records?',
    hitStatus: 'error',
    decides: (s) => !s || !s.status,
    rule: () => 'If the check itself failed or the dates were unusable, the practice gets no status at all and is left out of the totals. A failed check is never read as "never used" or "healthy".',
    hit: (s) => `No: ${errorText(s && s.error)}. It gets no status and is left out of the totals.`,
    clear: () => 'Yes.',
  },
  {
    id: 'ever',
    question: 'Has it ever created a follow-up?',
    hitStatus: 'onboarding',
    decides: (s) => s.status === 'onboarding',
    rule: (rules) => {
      const { o } = limits(rules);
      return `If it has never created a follow-up it is judged by how long ago it signed up: on track before day ${o.slowDay}, slow from day ${o.slowDay}, stalled from day ${o.stalledDay}. A stalled practice is shown as "No follow-ups yet".`;
    },
    hit: (s, rules) => {
      const { o } = limits(rules);
      const age = s.siteAgeDays;
      if (typeof age !== 'number') return `No follow-up on record, and the sign-up date isn't on record, so we can't say how long it has waited. (On track before day ${o.slowDay}, slow from day ${o.slowDay}, stalled from day ${o.stalledDay}.)`;
      const stage = s.onboardingStage === 'stalled' ? 'stalled' : s.onboardingStage === 'slow' ? 'slow' : 'on track';
      return `No. It signed up ${plural(age, 'day')} ago and has never created one. That is ${stage} (on track before day ${o.slowDay}, slow from day ${o.slowDay}, stalled from day ${o.stalledDay}).`;
    },
    clear: (s) => `Yes. The most recent one was ${formatDay(s.lastFollowupDay)}.`,
  },
  {
    id: 'atrisk',
    question: 'Has it been quiet longer than the At-risk limit?',
    hitStatus: 'at_risk',
    decides: (s) => s.status === 'at_risk',
    rule: (rules) => {
      const { t } = limits(rules);
      return `Quiet means business days (Monday to Friday) since its last follow-up. More than ${plural(t.atRiskQuietBusinessDays, 'business day')} of quiet is At risk, however busy it used to be.`;
    },
    hit: (s, rules) => {
      const { r, t } = limits(rules);
      const few = s.activeDays180 === 0 ? 'no active days' : `only ${plural(s.activeDays180, 'active day')}`;
      const extra = s.established === false ? ` It also has ${few} with follow-ups in the last ${r.establishedLookbackDays}, so it was never a regular user.` : '';
      return `Yes. Its last follow-up was ${formatDay(s.lastFollowupDay)}, ${plural(s.quietBusinessDays, 'business day')} ago, past the limit of ${t.atRiskQuietBusinessDays}${relaxedNote(t)}.${extra}`;
    },
    clear: (s, rules) => {
      const { t } = limits(rules);
      return `No. ${plural(s.quietBusinessDays, 'business day')} since its last follow-up (${formatDay(s.lastFollowupDay)}) is within the limit of ${t.atRiskQuietBusinessDays}${relaxedNote(t)}.`;
    },
  },
  {
    id: 'established',
    question: 'Does it have enough follow-up history to be judged on its normal pattern?',
    hitStatus: 'low_activity',
    decides: (s) => s.status === 'low_activity',
    rule: (rules) => {
      const { r } = limits(rules);
      return `A practice needs at least ${plural(r.establishedMinActiveDays, 'active day')} with follow-ups in the last ${r.establishedLookbackDays} days to be judged on Watch. With fewer it is shown as Low activity, which is informational and never alerts.`;
    },
    hit: (s, rules) => {
      const { r } = limits(rules);
      return `No. Only ${plural(s.activeDays180, 'active day')} with follow-ups in the last ${r.establishedLookbackDays}, and ${r.establishedMinActiveDays} are needed. It is not quiet enough to be At risk, so it is shown as Low activity and not judged on Watch.`;
    },
    clear: (s, rules) => {
      const { r } = limits(rules);
      return `Yes. ${plural(s.activeDays180, 'active day')} with follow-ups in the last ${r.establishedLookbackDays} (${r.establishedMinActiveDays} needed).`;
    },
  },
  {
    id: 'watchquiet',
    question: 'Has it been quiet longer than the Watch limit?',
    hitStatus: 'watch',
    decides: (s) => s.status === 'watch' && s.reason !== 'decline_with_quiet_stretch',
    rule: (rules) => {
      const { t } = limits(rules);
      return `More than ${plural(t.watchQuietBusinessDays, 'business day')} of quiet, but not more than ${t.atRiskQuietBusinessDays} (that would already be At risk), is Watch.`;
    },
    hit: (s, rules) => {
      const { t } = limits(rules);
      return `Yes. Its last follow-up was ${formatDay(s.lastFollowupDay)}, ${plural(s.quietBusinessDays, 'business day')} ago, past the limit of ${t.watchQuietBusinessDays}${relaxedNote(t)}.`;
    },
    clear: (s, rules) => {
      const { t } = limits(rules);
      return `No. ${plural(s.quietBusinessDays, 'business day')} of quiet is within the limit of ${t.watchQuietBusinessDays}${relaxedNote(t)}.`;
    },
  },
  {
    id: 'decline',
    question: 'Has its pace dropped sharply while it has gone quiet?',
    hitStatus: 'watch',
    decides: (s) => s.status === 'watch' && s.reason === 'decline_with_quiet_stretch',
    rule: (rules) => {
      const { t } = limits(rules);
      const p = paceTerms(rules);
      return `Watch when ${p.recent} is below ${Math.round(t.declineWatchRatio * 100)}% of ${p.earlier} and it has been quiet more than ${plural(t.p95QuietBusinessDays, 'business day')}. Both must be true: a dip alone is too noisy to flag.`;
    },
    hit: (s, rules) => {
      const { t } = limits(rules);
      const p = paceTerms(rules);
      const pct = typeof s.declineRatio === 'number' ? Math.round(s.declineRatio * 100) : null;
      return `Yes. ${upFirst(p.recent)} is ${pct == null ? 'well below' : `${pct}% of`} ${p.earlier} (Watch is below ${Math.round(t.declineWatchRatio * 100)}%), and it has been quiet ${plural(s.quietBusinessDays, 'business day')} (needs more than ${t.p95QuietBusinessDays})${relaxedNote(t)}.`;
    },
    clear: (s, rules) => {
      const { t } = limits(rules);
      const p = paceTerms(rules);
      if (typeof s.declineRatio !== 'number') return `No. It has no earlier pace to compare with (no follow-ups ${p.priorPeriod}), so this check cannot fire.`;
      const pct = Math.round(s.declineRatio * 100);
      const slow = s.declineRatio < t.declineWatchRatio;
      return slow
        ? `No. ${upFirst(p.recent)} is ${pct}% of ${p.earlier}, which is low, but it has only been quiet ${plural(s.quietBusinessDays, 'business day')} (needs more than ${t.p95QuietBusinessDays}).`
        : `No. ${upFirst(p.recent)} is ${pct}% of ${p.earlier} (Watch is below ${Math.round(t.declineWatchRatio * 100)}%).`;
    },
  },
  {
    id: 'healthy',
    question: 'None of the above applied.',
    hitStatus: 'healthy',
    decides: (s) => s.status === 'healthy',
    rule: () => 'A practice that passes every check above is Healthy: it is within its normal pattern.',
    hit: (s) => `Healthy: last follow-up ${formatDay(s.lastFollowupDay)}, within its normal pattern.`,
    clear: () => 'Nothing above applied.',
  },
];

const NOT_REACHED = 'Not checked: an earlier check already set the status.';

/**
 * The ordered list of checks behind ONE row: `[{ n, id, question, outcome, answer, status }]`.
 * `outcome` is 'passed' (checked, did not apply), 'decided' (this check set the status; `status` is its
 * display key) or 'skipped' (an earlier check had already decided). Returns [] for a row the server sent
 * without a usable status/reason pairing, so the panel falls back to the one-line sentence.
 */
export function explainChecks(site, rules) {
  const s = site || {};
  const key = displayKey(s);
  const decidedAt = CHECK_STEPS.findIndex((step) => step.decides(s));
  if (decidedAt === -1) return [];
  return CHECK_STEPS.map((step, i) => {
    const base = { n: i + 1, id: step.id, question: step.question };
    if (i < decidedAt) return { ...base, outcome: 'passed', answer: step.clear(s, rules), status: null };
    if (i === decidedAt) return { ...base, outcome: 'decided', answer: step.hit(s, rules), status: key };
    return { ...base, outcome: 'skipped', answer: NOT_REACHED, status: null };
  });
}

/**
 * The legend: every rule in the order it is checked, with today's limits. `[{ n, question, rule, result }]`.
 * `result` says what a practice is called when that rule decides it.
 */
export function ruleLegend(rules) {
  return CHECK_STEPS.map((step, i) => ({
    n: i + 1,
    question: step.question,
    rule: step.rule(rules),
    result: step.hitStatus === 'onboarding'
      ? `${BY_KEY.onboarding.label}, or "${BY_KEY.no_followups.label}" once stalled`
      : BY_KEY[step.hitStatus].label,
  }));
}

/** "11-20" -> "Nov 20". null when it is not a month-day. */
function monthDay(md) {
  const m = /^(\d{2})-(\d{2})$/.exec(typeof md === 'string' ? md : '');
  const month = m && MONTHS[Number(m[1]) - 1];
  return month ? `${month} ${Number(m[2])}` : null;
}

/**
 * The sentences under the rule list: what "quiet" means, what the other features do, and the holiday
 * relaxation window (read from the echoed rules). `[{ key, text }]`.
 */
export function ruleNotes(rules) {
  const r = rules || {};
  const h = r.holiday || {};
  const from = monthDay(h.startMonthDay);
  const to = monthDay(h.endMonthDay);
  const notes = [
    { key: 'business-days', text: 'A business day is Monday to Friday. There is no holiday calendar, so a public holiday on a weekday counts as a business day.' },
    { key: 'anchor', text: 'Only follow-ups decide the status, because they are the one thing every established practice does. Conversations, Forms, Reminders, Campaigns and Workflows are shown as chips for context and never change a status.' },
    { key: 'first-match', text: 'The checks run top to bottom and the first one that applies sets the status; the rest are skipped, so a practice is never given two statuses.' },
  ];
  if (from && to && typeof h.multiplier === 'number') {
    notes.push({ key: 'holiday', text: `From ${from} to ${to} the quiet limits (the At-risk, Watch and decline limits above) are multiplied by ${h.multiplier}, because offices are quieter around the holidays. This is an assumption, not measured.` });
  }
  return notes;
}

/** Summary chips counted from the ROWS (see COPY RULE). Errors show only when there are some. */
export function summaryChips(sites) {
  const counts = {};
  (sites || []).forEach((s) => { const k = displayKey(s); counts[k] = (counts[k] || 0) + 1; });
  return DISPLAY_STATUSES
    .filter((s) => s.key !== 'error' || counts.error)
    .map((s) => ({ ...s, count: counts[s.key] || 0 }));
}

/** One feature's chip on a row: `{ key, label, state, text }` with a hover `title` that says exactly what it means. */
export function featureChip(feature) {
  const f = feature || {};
  const label = f.label || f.key || 'Feature';
  let state;
  let title;
  if (f.verdict === 'adopted') {
    state = 'adopted';
    title = `${label}: used in the last ${plural(f.windowDays, 'day')} (${f.countInWindow} times). Last used ${formatDay(f.lastActiveDay)}.`;
  } else if (f.verdict === 'dormant') {
    state = 'dormant';
    title = `${label}: used before (last ${formatDay(f.lastActiveDay)}), but not in the last ${plural(f.windowDays, 'day')}.`;
  } else if (f.verdict === 'never') {
    state = 'never';
    title = `${label}: never used.`;
  } else {
    state = 'error';
    title = `${label}: could not be checked (${errorText(f.error)}).`;
  }
  return { key: f.key, label, state, title };
}

/** How many practices use each feature right now, from the response's own adoption block. */
export function adoptionLine(summary, sites) {
  const adoption = (summary && summary.adoption) || {};
  const first = (sites || [])[0];
  const labels = {};
  ((first && first.features) || []).forEach((f) => { labels[f.key] = f.label; });
  return Object.keys(adoption).map((k) => ({ key: k, label: labels[k] || k, adopted: adoption[k].adopted }));
}

/** "Fresh from the database" / "Saved copy, 4 min old". */
export function cacheNote(cache) {
  if (!cache) return '';
  if (!cache.hit) return 'Fresh from the database';
  const mins = Math.floor((cache.ageSeconds || 0) / 60);
  return mins < 1 ? 'Saved copy, under a minute old' : `Saved copy, ${plural(mins, 'min')} old`;
}

// ---- Nightly history (plan section 23): "changed since yesterday" and a small per-row history ----------------
// The server saves one status per practice per night and sends, for every row, `previous` (the latest saved night
// BEFORE today) and `history` (the last two weeks of nights). Nothing is recomputed here: a night's status is shown
// through the same displayKey() as the live one, so "No follow-ups yet" reads the same in both places.

/** How serious a display key is, only to say whether a change is for the worse or the better. */
const SEVERITY = { at_risk: 3, watch: 2, no_followups: 2, onboarding: 1, low_activity: 1, healthy: 0 };

/** A saved night as a display key, or null when that night's check had failed (a gap is "could not tell"). */
function nightKey(night) {
  if (!night || !night.status) return null;
  return displayKey({ status: night.status, onboardingStage: night.onboardingStage });
}

/**
 * How a row differs from its last saved night: `{ from, to, fromLabel, toLabel, direction, day, text }`, or null
 * when it does not differ, there is no earlier night, or either side could not be checked.
 * `direction` is 'worse' / 'better' by severity, or 'moved' when the two sit at the same level.
 */
export function changeSince(site) {
  const was = nightKey(site && site.previous);
  if (!was || !site.status) return null;
  const now = displayKey(site);
  if (now === 'error' || now === was) return null;
  const before = SEVERITY[was];
  const after = SEVERITY[now];
  const direction = after > before ? 'worse' : after < before ? 'better' : 'moved';
  return {
    from: was,
    to: now,
    fromLabel: BY_KEY[was].label,
    toLabel: BY_KEY[now].label,
    direction,
    day: site.previous.day,
    // No year: it sits in a narrow column and the line above the table gives the full date.
    text: `Was ${BY_KEY[was].label} on ${formatDay(site.previous.day).replace(/, \d{4}$/, '')}`,
  };
}

/** The row's recent nights as dots, oldest first: `{ day, key, label, title }`. A failed night is "Couldn't check". */
export function historyDots(site) {
  const nights = site && Array.isArray(site.history) ? site.history : [];
  return nights.map((n) => {
    const key = nightKey(n) || 'error';
    const label = BY_KEY[key].label;
    return { day: n.day, key, label, title: `${formatDay(n.day)}: ${label}` };
  });
}

/** One sentence for the whole strip, for a screen reader: "Last 3 nights: Healthy, Watch, At risk". */
export function historyLabel(dots) {
  const list = dots || [];
  return `Last ${plural(list.length, 'night')}: ${list.map((d) => d.label).join(', ')}`;
}

/**
 * The line above the table: how many practices changed since the last saved night. null when there is nothing to
 * compare with yet (no history, or no practice has an earlier night), so the panel says nothing rather than "0".
 */
export function changesLine(sites, history) {
  if (!history || history.available !== true) return null;
  const list = sites || [];
  const baselines = list.filter((s) => nightKey(s.previous)).map((s) => s.previous.day).sort();
  if (!baselines.length) return null;
  const since = formatDay(baselines[baselines.length - 1]);
  const changed = list.map(changeSince).filter(Boolean);
  if (!changed.length) return `No practice has changed status since the last saved night (${since}).`;
  const count = (d) => changed.filter((c) => c.direction === d).length;
  const parts = [['worse', count('worse')], ['better', count('better')], ['moved sideways', count('moved')]]
    .filter(([, n]) => n > 0)
    .map(([word, n]) => `${n} ${word}`);
  return `Changed since the last saved night (${since}): ${changed.length} of ${list.length} (${parts.join(', ')}).`;
}

/** The small note about the saved nights, or null for an older server that does not send any. */
export function historyNote(history) {
  if (!history || typeof history !== 'object') return null;
  if (history.available === false) return "Nightly history couldn't be loaded just now. The table above is not affected.";
  if (!history.nights) return "Nightly history starts after tonight's first saved night.";
  if (history.nights === 1) return `One night saved so far (${formatDay(history.lastDay)}). "Changed since" shows from the next night on.`;
  return `${history.nights} nights saved (${formatDay(history.firstDay)} to ${formatDay(history.lastDay)}). Each dot is one night, oldest on the left.`;
}
